import { merge, Observable, ReplaySubject, Subject, Subscription, timer } from 'rxjs';
import { first, map, skip, switchMap, take, throttleTime } from 'rxjs/operators';

const REPLAY_SIZE = 1;
export const DEFAULT_CACHE_TIME = 600000; // 10min

export class CachedRequestResponse<T> {
  private requestSubscription!: Subscription;
  private readonly request: Subject<void>;
  private readonly responseSubject: Subject<T>;
  private readonly response$: Observable<T>;
  private readonly forceRefresh$: Subject<void>;

  constructor(producerFactory: () => Observable<T>, cacheTime: number, autoRefresh = false) {
    this.request = new Subject();
    this.responseSubject = new ReplaySubject<T>(REPLAY_SIZE);
    this.response$ = this.responseSubject.asObservable();
    this.forceRefresh$ = new Subject();

    this.setupRequestResponse(producerFactory, cacheTime, autoRefresh);
  }

  private setupRequestResponse(producerFactory: () => Observable<T>, cacheTime: number, autoRefresh: boolean) {
    const throttledRequest$ = this.request.pipe(
      // Throttle to invalidate cache after cacheTime. Trailing events should not trigger request.
      throttleTime(cacheTime),
    );

    const pipedRequest$ = autoRefresh
      ? throttledRequest$.pipe(
          // timer forces requests with cacheTime cadence
          switchMap(() => timer(0, cacheTime)),
        )
      : throttledRequest$;

    // force refresh when requested
    this.requestSubscription = merge(pipedRequest$, this.forceRefresh$).subscribe(() => {
      // first() forces auto-closure after first emission and spares us handling the unsubscription
      producerFactory()
        .pipe(first())
        .subscribe({ next: (response) => this.responseSubject.next(response), error: (error) => this.responseSubject.error(error) });
    });
  }

  public get(): Observable<T> {
    this.request.next();
    return this.response$;
  }

  public refresh(): Observable<void> {
    this.forceRefresh$.next();
    return this.responseSubject.pipe(
      // Avoid immediate firing of the cached value
      skip(REPLAY_SIZE),
      // Notify on the completion of the actual cache-refresh request
      take(1),
      // Don't return the new response data as to avoid misuse of this method
      map(() => undefined),
    );
  }

  public unsubscribe(): void {
    this.requestSubscription.unsubscribe();
  }
}
